import React from 'react';
import { NavLink, useLocation, matchPath } from 'react-router-dom';
import {
  Drawer, List, ListItemButton, ListItemIcon,
  ListItemText, Box, Collapse, useMediaQuery, useTheme
} from '@mui/material';
import {
  Home,
  Image,
  HelpOutline,
  ContactSupport,
  Category,
  Person,
  Store,
  ExpandLess,
  ExpandMore,
  Fastfood,
  LocalGroceryStore,
  Map,
  DirectionsBike,
  AccessTime,
  Receipt,
  Settings,
  LocalOffer,
  BuildCircle,
  Payment,
  AccountBalanceWallet,
  BusinessCenter,
  Notifications,
  Article,
  People,
  Dashboard,
  CreditCard,
  ToggleOn,
} from '@mui/icons-material';
import { useAdminContext } from '../context/AdminContext';
import careoffShortLogo from '../assets/careoff-short-logo.svg';
import careoffLogo from '../assets/careoff-logo.svg';

// ─── Nav Structure ────────────────────────────────────────────────────────────
const navItems = [
  {
    label: 'Home',
    icon: Home,
    path: '/',
    end: true,
  },

  {
    label: 'Peoples',
    icon: People,
    path: '/peoples',
    children: [
      { label: 'Users', path: '/users', icon: Person },
      { label: 'Delivery Partners', path: '/partners', icon: DirectionsBike },
    ],
  },
  {
    label: 'Categories', path: '/categories', icon: Category 
  },
  {
    label: 'Services',
    icon: BusinessCenter,
    path: '/services',
    children: [
      { label: 'Service Bookings', path: '/bookings/service-bookings', icon: Receipt },
      { label: 'All Services', path: '/services', icon: Dashboard, end: true },
    ],
  },
  {
    label: 'Grocery',
    icon: LocalGroceryStore,
    path: '/grocery',
    children: [
      { label: 'Grocery Bookings', path: '/bookings/grocery-bookings', icon: Receipt },
      { label: 'Grocery Vendors', path: '/vendors', icon: Store, end: true },
    ],
  },
  {
    label: 'Food',
    icon: Fastfood,
    path: '/food',
    children: [
      { label: 'Food Bookings', path: '/bookings/food-bookings', icon: Receipt },
      { label: 'Food Vendors', path: '/vendors/food', icon: Store },
      { label: 'Food Categories', path: '/food-categories', icon: Category },
    ],
  },
   {
    label: 'Configuration',
    icon: BuildCircle,
    path: '/configuration',
    children: [
      { label: 'Service Locations', path: '/service-location', icon: Map },
      { label: 'Time Slots', path: '/timeslots/all', icon: AccessTime },
      { label: 'Service Configuration', path: '/service-configs', icon: Settings },
    ],
  },
  {
    label: 'Financials & Offers',
    icon: CreditCard,
    path: '/financials',
    children: [
      { label: 'Transactions', path: '/payment-transactions', icon: Payment },
      { label: 'Vendor Payouts', path: '/vendor-wallet/payouts', icon: AccountBalanceWallet },
      { label: 'Coupons', path: '/coupons', icon: LocalOffer },
    ],
  },
  {
    label: 'App Content & Settings',
    icon: Settings,
    path: '/app-content',
    children: [
      { label: 'Banners', path: '/banners', icon: Image },
      { label: 'Features', path: '/features', icon: ToggleOn },
      { label: 'Push Notifications', path: '/push-notifications', icon: Notifications },
      { label: 'CMS Content', path: '/cms', icon: Article },
      { label: 'FAQs', path: '/faqs', icon: HelpOutline },
      { label: 'Contact Us', path: '/contact', icon: ContactSupport },
    ],
  },
];

// ─── Helper: does any child path match current location? ──────────────────────
const isGroupActive = (children, pathname) =>
  children?.some((child) =>
    matchPath({ path: child.path, end: child.end ?? false }, pathname)
  );

// ─── Sidebar Component ─────────────────────────────────────────────────────────
const Sidebar = () => {
  const { sidebarOpen, setSidebarOpen } = useAdminContext();
  const location = useLocation();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Build initial open-state: open a group if one of its children is active
  const buildInitialOpen = () =>
    navItems.reduce((acc, item) => {
      if (item.children) {
        acc[item.label] = isGroupActive(item.children, location.pathname);
      }
      return acc;
    }, {});

  const [openMenus, setOpenMenus] = React.useState(buildInitialOpen);

  // Accordion: opening one group closes all others
  const toggleMenu = (label) => {
    setOpenMenus((prev) => {
      const isAlreadyOpen = prev[label];
      // Close everything, then toggle the clicked one
      const allClosed = Object.keys(prev).reduce((acc, key) => {
        acc[key] = false;
        return acc;
      }, {});
      return { ...allClosed, [label]: !isAlreadyOpen };
    });
  };

  const drawerWidth = isMobile ? 240 : sidebarOpen ? 240 : 56;
  const isExpanded = isMobile ? true : sidebarOpen;

  // ── Shared styles ────────────────────────────────────────────────────────────
  const parentItemSx = (isActive, isOpen) => ({
    pl: 1.5,
    minHeight: 38,
    mb: isOpen ? 0 : 1, // No gap when open
    mx: 0.75,
    borderRadius: isOpen ? '8px 8px 0 0' : '8px',
    color: 'text.primary',
    transition: 'background-color 0.18s ease, color 0.18s ease',
    '& .MuiListItemIcon-root': { color: 'inherit', transition: 'color 0.18s ease' },
    ...(isActive
      ? {
          backgroundColor: 'primary.main',
          color: 'white',
          '& .MuiListItemIcon-root': { color: 'white' },
          '&:hover': {
            backgroundColor: 'primary.dark',
            color:"white",
            '& .MuiListItemIcon-root': { color: 'white' },
          },
        }
      : {
          '&:hover': {
            backgroundColor: 'rgba(0,0,0,0.05)',
            color: 'primary.main',
            '& .MuiListItemIcon-root': { color: 'black' },
          },
        }),
  });

  const childItemSx = (isActive, isLast) => ({
    pl: isExpanded ? 5.75 : 1.5, // Use padding for indentation instead of margin
    pr: 1,
    minHeight: 34,
    mb: isLast ? 1 : 0, // Gap only after the last child
    mx: 0.75, // Full width matching parent
    borderRadius: isLast ? '0 0 8px 8px' : '0', // Round bottom corners of last item
    color: 'text.primary',
    backgroundColor: 'rgba(0,0,0,0.06)', // Slightly darker persistent background to differentiate from parent
    transition: 'background-color 0.18s ease, color 0.18s ease, margin 0.22s ease',
    '& .MuiListItemIcon-root': { color: 'inherit', transition: 'color 0.18s ease' },
    ...(isActive
      ? {
          backgroundColor: 'primary.light',
          color: 'primary.main',
          fontWeight: 700,
          '& .MuiListItemIcon-root': { color: 'white' },
          '&:hover': {
            backgroundColor: 'primary.light',
            '& .MuiListItemIcon-root': { color: 'white' },
          },
        }
      : {
          '&:hover': {
            backgroundColor: 'rgba(0,0,0,0.08)',
            color: 'primary.main',
            '& .MuiListItemIcon-root': { color: 'primary.main' },
          },
        }),
  });

  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'permanent'}
      open={sidebarOpen}
      onClose={() => isMobile && setSidebarOpen(false)}
      ModalProps={{ keepMounted: true }}
      PaperProps={{
        sx: {
          width: drawerWidth,
          overflowX: 'hidden',
          overflowY: 'auto',
          transition: 'width 0.25s cubic-bezier(0.4,0,0.2,1)',
          boxSizing: 'border-box',
          borderRight: '1px solid',
          borderColor: 'divider',
          boxShadow: 'none',
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        },
      }}
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        transition: 'width 0.25s cubic-bezier(0.4,0,0.2,1)',
        '& .MuiDrawer-paper': {
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        },
      }}
    >
      {/* ── Logo ── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 56,
          px: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          mb: 0.5,
        }}
      >
        <Box
          component="img"
          src={isExpanded ? careoffLogo : careoffShortLogo}
          alt="CareOff"
          sx={{ height: isExpanded ? 36 : 38, transition: 'height 0.22s ease' }}
        />
      </Box>

      {/* ── Nav Items ── */}
      <List sx={{ pt: 0.5, pb: 2 }}>
        {navItems.map(({ label, icon: Icon, path, end, children }) => {
          // ── Parent with children ───────────────────────────────────────────
          if (children) {
            const isOpen = !!openMenus[label];
            const isActive = isGroupActive(children, location.pathname);

            return (
              <React.Fragment key={label}>
                {/* Section label shown only when collapsed (icon-only mode) */}
                {!isExpanded && (
                  <Box sx={{ height: 4 }} />
                )}

                <ListItemButton
                  onClick={() => toggleMenu(label)}
                  sx={parentItemSx(isActive && !isOpen, isOpen)}
                >
                  <ListItemIcon sx={{ minWidth: isExpanded ? 38 : 'auto', justifyContent: 'center' }}>
                    <Icon fontSize="small" />
                  </ListItemIcon>

                  {isExpanded && (
                    <>
                      <ListItemText
                        primary={label}
                        primaryTypographyProps={{ fontWeight: 600, fontSize: '0.875rem' }}
                        sx={{ opacity: 1, transition: 'opacity 0.18s ease' }}
                      />
                      <Box
                        sx={{
                          ml: 'auto',
                          display: 'flex',
                          alignItems: 'center',
                          transition: 'transform 0.22s ease',
                          transform: isOpen ? 'rotate(0deg)' : 'rotate(0deg)',
                        }}
                      >
                        {isOpen ? (
                          <ExpandLess fontSize="small" sx={{ opacity: 0.6 }} />
                        ) : (
                          <ExpandMore fontSize="small" sx={{ opacity: 0.6 }} />
                        )}
                      </Box>
                    </>
                  )}
                </ListItemButton>

                {/* Children collapse — slides open vertically, each child slides in left→right */}
                <Collapse
                  in={isOpen && isExpanded}
                  timeout={{ enter: 240, exit: 160 }}
                  unmountOnExit
                >
                  <List component="div" disablePadding sx={{ mb: 0, pt: 0 }}>
                    {children.map((child, childIdx) => {
                      const ChildIcon = child.icon;
                      const isChildActive = !!matchPath(
                        { path: child.path, end: child.end ?? false },
                        location.pathname
                      );
                      const isLast = childIdx === children.length - 1;
                      return (
                        <ListItemButton
                          key={child.label}
                          component={NavLink}
                          to={child.path}
                          end={child.end}
                          selected={isChildActive}
                          sx={{
                            ...childItemSx(isChildActive, isLast),
                            // Slide-in from left keyframe
                            '@keyframes slideInFromLeft': {
                              '0%': {
                                opacity: 0,
                                transform: 'translateX(-18px)',
                              },
                              '100%': {
                                opacity: 1,
                                transform: 'translateX(0)',
                              },
                            },
                            animation: isOpen
                              ? `slideInFromLeft 0.22s cubic-bezier(0.25,0.46,0.45,0.94) both`
                              : 'none',
                            animationDelay: isOpen ? `${childIdx * 45}ms` : '0ms',
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 30, justifyContent: 'center' }}>
                            <ChildIcon fontSize="small" />
                          </ListItemIcon>
                          {isExpanded && (
                            <ListItemText
                              primary={child.label}
                              primaryTypographyProps={{
                                fontSize: '0.825rem',
                                fontWeight: isChildActive ? 600 : 400,
                              }}
                            />
                          )}
                        </ListItemButton>
                      );
                    })}
                  </List>
                </Collapse>
              </React.Fragment>
            );
          }

          // ── Standalone item ────────────────────────────────────────────────
          const isActive = end
            ? !!matchPath({ path, end: true }, location.pathname)
            : !!matchPath({ path: `${path}/*`, end: false }, location.pathname);

          return (
            <ListItemButton
              key={label}
              component={NavLink}
              to={path}
              end={end}
              selected={isActive}
              sx={{
                pl: 1.5,
                minHeight: 38,
                mb: 1,
                mx: 0.75,
                borderRadius: '8px',
                color: 'text.primary',
                transition: 'background-color 0.18s ease, color 0.18s ease',
                '& .MuiListItemIcon-root': { color: 'inherit', transition: 'color 0.18s ease' },
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                  '& .MuiListItemIcon-root': { color: 'white' },
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                    '& .MuiListItemIcon-root': { color: 'white' },
                  },
                },
                '&:not(.Mui-selected):hover': {
                  backgroundColor: 'rgba(0,0,0,0.05)',
                  color: 'primary.main',
                  '& .MuiListItemIcon-root': { color: 'primary.main' },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: isExpanded ? 38 : 'auto', justifyContent: 'center' }}>
                <Icon fontSize="small" />
              </ListItemIcon>
              {isExpanded && (
                <ListItemText
                  primary={label}
                  primaryTypographyProps={{ fontWeight: 600, fontSize: '0.875rem' }}
                />
              )}
            </ListItemButton>
          );
        })}
      </List>
    </Drawer>
  );
};

export default Sidebar;
